import { useState } from 'react';
import { X, Menu, Link, Copy, Check, Eye, EyeOff, AlignCenterIcon } from 'lucide-react';
import { T } from '../theme';
import { createReviewLink } from '../utils/reviewStorage';

export default function CreateReviewLinkModal({ user, fileName, expenseCount, defaultCurrency, onClose }) {
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [createdId, setCreatedId] = useState(null);
  const [copied, setCopied] = useState(false);

  const reviewUrl = createdId
    ? `${window.location.origin}/?review=${createdId}`
    : null;

  const handleCreate = async () => {
    if (!expenseCount) { setError('No expenses in this report.'); return; }
    setCreating(true);
    setError('');
    try {
      const id = await createReviewLink({
        ownerUid: user.uid,
        fileName,
        ownerName: user.displayName || user.email || null,
        ownerDefaultCurrency: defaultCurrency || 'USD',
        password: password.trim() || null,
      });
      setCreatedId(id);
    } catch (err) {
      setError('Failed to create link. Please try again.');
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(reviewUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
  const box = { background: T.navy, borderRadius: 16, padding: 28, width: '100%', maxWidth: 420, color: '#fff', position: 'relative' };
  const label = { fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, display: 'block' };
  const input = { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.07)', color: '#fff', fontSize: 14, boxSizing: 'border-box', outline: 'none' };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={box}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)' }}>
          <X size={18} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{ background: T.tealDeep, borderRadius: 10, padding: 8 }}><Link size={18} color={T.tealBright} /></div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Create Live Link for Review</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{fileName} · {expenseCount || 0} expenses</div>
          </div>
        </div>

        {!createdId ? (
          <>
            <div style={{ marginBottom: 20 }}>
              <label style={label}>Password (optional)</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="Leave blank for no password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={{ ...input, paddingRight: 40 }}
                />
                <button
                  onClick={() => setShowPw(v => !v)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)' }}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
                The recipient will need this password to view the report.
              </div>
            </div>

            {error && <div style={{ fontSize: 13, color: '#f87171', marginBottom: 16 }}>{error}</div>}

            <button
              onClick={handleCreate}
              disabled={creating}
              style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: T.gradTeal, color: '#fff', fontSize: 15, fontWeight: 700, cursor: creating ? 'not-allowed' : 'pointer', opacity: creating ? 0.7 : 1 }}
            >
              {creating ? 'Creating…' : 'Generate Link'}
            </button>
          </>
        ) : (
          <>
            <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Share this link with anyone that needs to review and ask questions about this report. e.g. the accounting or reimbursement team. 
                Anyone with this will get a read-only live view of this report. 
                To view comments/questions from the reviewer, navigate to "Review Links" under the&nbsp;
                <span style={{ verticalAlign: "middle" }}><Menu size={14} /></span> menu in the top left corner.</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 12, color: T.tealBright, wordBreak: 'break-all', flex: 1 }}>{reviewUrl}</div>
                <button
                  onClick={copyUrl}
                  style={{ flexShrink: 0, padding: '8px 12px', borderRadius: 8, border: 'none', background: copied ? T.tealDeep : 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700 }}
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            {password && (
              <div style={{ background: 'rgba(242,169,59,0.12)', border: '1px solid rgba(242,169,59,0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: T.amberBright }}>
                Password: <strong>{password}</strong> — share this with the reviewer separately.
              </div>
            )}
            <button onClick={onClose} style={{ width: '100%', padding: '12px', borderRadius: 12, border: 'none', background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}
